"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Bar, ComposedChart, CartesianGrid, Line,
  XAxis, YAxis, Legend, Tooltip, ResponsiveContainer,
} from "recharts";
import { CircleHelp, Download, ChevronDown } from "lucide-react";
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import axiosInstance from "@/api/axios";
import { ChartContainer } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getInvertedColorScheme } from "@/lib/colorSchemes";

const STREAMS = ["Organic", "PMD", "Paper", "Residual"];

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toISOHour = (d, h) => `${toISO(d)}T${pad(Math.min(h, 23))}:00:00`;

const buildPeriods = (timeRange, customDateRange, isCustomDateRange) => {
  const now = new Date();

  if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
    const s    = new Date(customDateRange[0].startDate);
    const e    = new Date(customDateRange[0].endDate);
    const days = Math.round((e - s) / 86400000);

    if (days === 0) {
      return Array.from({ length: 24 }, (_, h) => ({
        label: `${pad(h)}:00`, start: toISOHour(s, h),
        end: toISOHour(s, h === 23 ? 23 : h + 1), hourBucket: true,
      }));
    }

    if (days <= 35) {
      const periods = [];
      for (let i = 0; i <= days; i++) {
        const d = new Date(s); d.setDate(d.getDate() + i);
        periods.push({
          label: `${pad(d.getDate())} ${d.toLocaleString("en", { month: "short" })}`,
          start: toISO(d), end: toISO(d),
        });
      }
      return periods;
    }

    const periods = [];
    const cur = new Date(s.getFullYear(), s.getMonth(), 1);
    while (cur <= e) {
      const mEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      periods.push({
        label: cur.toLocaleString("en", { month: "short", year: "2-digit" }),
        start: toISO(cur), end: toISO(mEnd > e ? e : mEnd),
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return periods;
  }

  const tr = (timeRange || "year").replace(/[\s_-]+/g, "").toLowerCase();

  if (tr === "day") {
    return Array.from({ length: 24 }, (_, h) => ({
      label: `${pad(h)}:00`, start: toISOHour(now, h),
      end: toISOHour(now, h === 23 ? 23 : h + 1), hourBucket: true,
    }));
  }

  if (tr === "week") {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now); d.setDate(d.getDate() - (6 - i));
      return {
        label: d.toLocaleString("en", { weekday: "short" }) + " " + pad(d.getDate()),
        start: toISO(d), end: toISO(d),
      };
    });
  }

  if (tr === "month") {
    const periods = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i);
      periods.push({
        label: `${pad(d.getDate())} ${d.toLocaleString("en", { month: "short" })}`,
        start: toISO(d), end: toISO(d),
      });
    }
    return periods;
  }

  const monthCount = tr === "3month" ? 3 : tr === "6month" ? 6 : 12;
  const periods = [];
  for (let i = monthCount - 1; i >= 0; i--) {
    const d    = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    periods.push({
      label: d.toLocaleString("en", { month: "short", year: "2-digit" }),
      start: toISO(d), end: toISO(mEnd),
    });
  }
  return periods;
};

const EDGE_POINT = {
  period: "",
  _edgePad: true,
  contamination_rate: 0,
  total_contaminated_kg: 0,
  total_items: 0,
  Organic_kg: 0, PMD_kg: 0, Paper_kg: 0, Residual_kg: 0,
  Organic_pct: 0, PMD_pct: 0, Paper_pct: 0, Residual_pct: 0,
};

const CustomTooltip = ({ active, payload, label, mode }) => {
  if (!active || !payload?.length) return null;
  const overall    = payload[0]?.payload?.contamination_rate;
  const totalKg    = payload[0]?.payload?.total_contaminated_kg;
  const totalItems = payload[0]?.payload?.total_items;

  const barEntries = payload.filter(
    (e) => e.type !== "line" && Number(e.value) > 0 && e.name !== "Total"
  );
  if (!label && !barEntries.length) return null;

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-sm min-w-[190px]">
      <p className="font-semibold mb-2">{label}</p>
      <div className="space-y-1">
        {barEntries.map((entry, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: entry.fill }} />
              <span className="text-muted-foreground">{entry.name}</span>
            </div>
            <span className="font-medium">
              {mode === "kg"
                ? `${Number(entry.value).toFixed(3)} kg`
                : `${Number(entry.value).toFixed(1)}%`}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t mt-2 pt-2 text-xs text-muted-foreground space-y-0.5">
        {mode === "percentage" && overall != null && (
          <div>Overall contamination: <span className="font-medium text-foreground">{Number(overall).toFixed(1)}%</span></div>
        )}
        {mode === "kg" && totalKg != null && (
          <div>Total contaminated: <span className="font-medium text-foreground">{Number(totalKg).toFixed(3)} kg</span></div>
        )}
        {totalItems != null && totalItems > 0 && (
          <div>Total items: <span className="font-medium text-foreground">{totalItems}</span></div>
        )}
      </div>
    </div>
  );
};

export function ContaminationTimeSeries({
  className,
  title,
  bins = [],
  binTypes = {},
  timeRange,
  customDateRange = [{ startDate: null, endDate: null, key: "selection" }],
  isCustomDateRange = false,
  ...props
}) {
  const exportRef = useRef(null);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [mode, setMode]           = useState("kg");

  // This chart intentionally uses the INVERTED palette relative to the
  // rest of the dashboard — see getInvertedColorScheme.
  const streamColors = React.useMemo(() => {
    const scheme = getInvertedColorScheme(bins, binTypes);
    return {
      Organic:  scheme.Organic,
      PMD:      scheme.PMD,
      Paper:    scheme.Paper,
      Residual: scheme.Residual,
    };
  }, [bins, binTypes]);

  const chartConfig = {
    Organic:  { label: "Organic",  color: streamColors.Organic  },
    PMD:      { label: "PMD",      color: streamColors.PMD      },
    Paper:    { label: "Paper",    color: streamColors.Paper     },
    Residual: { label: "Residual", color: streamColors.Residual  },
  };

  useEffect(() => {
    if (!bins || bins.length === 0) return;
    if (!timeRange && !isCustomDateRange) return;

    setIsLoading(true);
    setError(null);
    setChartData([]);

    const p = new URLSearchParams();
    bins.forEach((b) => p.append("bin", b));
    p.set("period", timeRange || "year");

    if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
      const s = customDateRange[0].startDate;
      const e = customDateRange[0].endDate;
      p.set("start_date", s instanceof Date ? toISO(s) : toISO(new Date(s)));
      p.set("end_date",   e instanceof Date ? toISO(e) : toISO(new Date(e)));
    }

    axiosInstance
      .get(`/contamination-timeseries/?${p.toString()}`)
      .then((res) => {
        const data = res.data;
        if (!Array.isArray(data)) { setError("Unexpected response"); return; }

        const shaped = data.map((item) => {
          const overall = item.contamination_rate ?? 0;
          const rawSum  = (item.organic_rate ?? 0) + (item.pmd_rate ?? 0) +
                          (item.paper_rate  ?? 0) + (item.residual_rate ?? 0);
          const scale = rawSum > 0 ? overall / rawSum : 0;

          return {
            period:                item.period,
            contamination_rate:    overall,
            total_contaminated_kg: item.total_contaminated_kg ?? 0,
            total_items:           item.total_items           ?? 0,
            Organic_pct:  (item.organic_rate  ?? 0) * scale,
            PMD_pct:      (item.pmd_rate      ?? 0) * scale,
            Paper_pct:    (item.paper_rate    ?? 0) * scale,
            Residual_pct: (item.residual_rate ?? 0) * scale,
            Organic_kg:   item.organic_kg  ?? 0,
            PMD_kg:       item.pmd_kg      ?? 0,
            Paper_kg:     item.paper_kg    ?? 0,
            Residual_kg:  item.residual_kg ?? 0,
          };
        });

        const withEdges = [{ ...EDGE_POINT }, ...shaped, { ...EDGE_POINT }];
        const N = withEdges.length;
        setChartData(
          withEdges.map((d, i) => {
            let pos;
            if (i === 0) pos = 0;
            else if (i === N - 1) pos = 1;
            else pos = (i + 0.5) / N;
            return { ...d, _pos: pos };
          })
        );
      })
      .catch(() => setError("Failed to load data"))
      .finally(() => setIsLoading(false));
  }, [
    timeRange,
    isCustomDateRange,
    JSON.stringify(bins),
    customDateRange?.[0]?.startDate instanceof Date
      ? customDateRange[0].startDate.toDateString()
      : String(customDateRange?.[0]?.startDate ?? ""),
    customDateRange?.[0]?.endDate instanceof Date
      ? customDateRange[0].endDate.toDateString()
      : String(customDateRange?.[0]?.endDate ?? ""),
  ]);

  const suffix        = mode === "kg" ? "_kg" : "_pct";
  const activeStreams = STREAMS.filter((s) =>
    chartData.some((d) => !d._edgePad && (d[`${s}${suffix}`] ?? 0) > 0)
  );

  const yMax = (() => {
    if (!chartData.length) return "auto";

    if (mode === "percentage") {
      const max = Math.max(...chartData.filter(d => !d._edgePad).map((d) => d.contamination_rate ?? 0));
      if (!max) return 100;
      return Math.ceil((max * 1.15) / 5) * 5;
    }

    const max = Math.max(...chartData.filter(d => !d._edgePad).map((d) =>
      STREAMS.reduce((sum, s) => sum + (d[`${s}_kg`] ?? 0), 0)
    ));
    if (!max) return "auto";
    return Math.ceil((max * 1.15) / 5) * 5;
  })();

  const handleExportCSV = () => {
    if (!chartData.length) return;
    const headers = [
      "Period", "Overall %", "Total Contaminated kg", "Total Items",
      "Organic %", "PMD %", "Paper %", "Residual %",
      "Organic kg", "PMD kg", "Paper kg", "Residual kg",
    ];
    const rows = chartData
      .filter(d => !d._edgePad)
      .map((d) =>
        [
          d.period,
          d.contamination_rate?.toFixed(2)    ?? "",
          d.total_contaminated_kg?.toFixed(3) ?? "",
          d.total_items ?? "",
          d.Organic_pct?.toFixed(2)  ?? "", d.PMD_pct?.toFixed(2)  ?? "",
          d.Paper_pct?.toFixed(2)    ?? "", d.Residual_pct?.toFixed(2) ?? "",
          d.Organic_kg?.toFixed(3)   ?? "", d.PMD_kg?.toFixed(3)   ?? "",
          d.Paper_kg?.toFixed(3)     ?? "", d.Residual_kg?.toFixed(3) ?? "",
        ].join(",")
      );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href     = URL.createObjectURL(blob);
    link.download = `contamination_timeseries_${timeRange || "data"}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const handleExportPNG = () => {
    if (!exportRef.current) return;
    const controls = exportRef.current.querySelector(".export-controls");
    if (controls) controls.style.display = "none";
    import("html2canvas").then((h) => {
      h.default(exportRef.current, { scale: 2, useCORS: true }).then((canvas) => {
        const link = document.createElement("a");
        link.download = `contamination_timeseries_${timeRange || "data"}.png`;
        link.href     = canvas.toDataURL("image/png");
        link.click();
      }).finally(() => { if (controls) controls.style.display = ""; });
    });
  };

  return (
    <div
      ref={exportRef}
      className={cn(
        "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section border-0 shadow-none",
        className
      )}
      {...props}
    >
      <div className="flex flex-wrap justify-between items-start gap-2 pb-2">
        <div>
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
            {title || "Contamination Rate Over Time"}
          </h4>
          {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {new Date(customDateRange[0].startDate).toLocaleDateString()} –{" "}
              {new Date(customDateRange[0].endDate).toLocaleDateString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 export-controls">
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
            <button
              onClick={() => setMode("kg")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mode === "kg"
                  ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >KG</button>
            <button
              onClick={() => setMode("percentage")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mode === "percentage"
                  ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >%</button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm"
                      className="text-muted-foreground hover:text-primary"
                      disabled={!chartData.length || isLoading}>
                <Download className="h-4 w-4 mr-1" />
                Export
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV}>Download as CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPNG}>Download as PNG</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <CircleHelp className="h-5 w-5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                {mode === "percentage"
                  ? "Bar height = actual overall contamination %. Segments show each stream's share."
                  : "Bar height = real contaminated kg per stream, directly from the scale data."}
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{ width: "100%", height: "100%" }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full w-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-destructive">{error}</div>
        ) : chartData.filter(d => !d._edgePad).length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[400px]">
            <div className="text-muted-foreground text-center">
              <p className="mb-2 text-lg">No data available</p>
              <p className="text-sm">Select a time range and bins to view contamination trends</p>
            </div>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 28, right: 30, left: 8, bottom: 20 }}
                barCategoryGap="20%"
                barGap={4}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />

                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 11 }}
                  tickLine={{ stroke: "hsl(var(--border))" }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  interval="preserveStartEnd"
                  padding={{ left: 0, right: 0 }}
                />

                <XAxis
                  xAxisId="lineAxis"
                  dataKey="_pos"
                  type="number"
                  domain={[0, 1]}
                  hide
                />

                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={{ stroke: "hsl(var(--border))" }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  tickFormatter={(v) => mode === "kg" ? `${v} kg` : `${v}%`}
                  domain={[0, yMax]}
                  width={mode === "kg" ? 64 : 44}
                />
                <Tooltip
                  content={<CustomTooltip mode={mode} />}
                  cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
                />
<Legend
  verticalAlign="bottom"
  iconType="square"
  iconSize={10}
  wrapperStyle={{ fontSize: 12, paddingTop: 14 }}
  payload={activeStreams.map((stream) => ({
    value: stream,
    type: "square",
    color: streamColors[stream],
  }))}
/>
                {activeStreams.map((stream, idx) => {
                  const isLast = idx === activeStreams.length - 1;
                  return (
                    <Bar
                      key={stream}
                      dataKey={`${stream}${suffix}`}
                      stackId="contamination"
                      fill={streamColors[stream]}
                      name={stream}
                      maxBarSize={60}
                      stroke="none"
                      strokeWidth={0}
                      shape={(props) => {
                        const { x, y, width, height, fill } = props;
                        if (!height || height <= 0) return null;

                        if (isLast) {
                          const r = Math.min(2, width / 2, height);
                          const path = `
                            M ${x},${y + height}
                            L ${x},${y + r}
                            Q ${x},${y} ${x + r},${y}
                            L ${x + width - r},${y}
                            Q ${x + width},${y} ${x + width},${y + r}
                            L ${x + width},${y + height}
                            Z
                          `;
                          return <path d={path} fill={fill} />;
                        }

                        return (
                          <g>
                            <rect x={x} y={y} width={width} height={height} fill={fill} />
                            <line x1={x} y1={y} x2={x + width} y2={y} stroke="white" strokeWidth={1.5} />
                          </g>
                        );
                      }}
                    />
                  );
                })}

                <Line
                  xAxisId="lineAxis"
                  dataKey={(d) =>
                    mode === "kg"
                      ? STREAMS.reduce((sum, s) => sum + (d[`${s}_kg`] ?? 0), 0)
                      : d.contamination_rate ?? 0
                  }
                  type="monotone"
                  dot={false}
                  activeDot={false}
                  stroke="#000000"
                  strokeWidth={2}
                  name="Total"
                  legendType="none"
                  isAnimationActive={false}
                />

              </ComposedChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </div>
    </div>
  );
}

export default ContaminationTimeSeries;